import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  ShoppingCart, Mail, Send, X, RefreshCw, AlertCircle, CheckCircle2, User,
} from 'lucide-react';
import axiosInstance from '../../api/axios';
import { Button } from '../../components/ui/button';
import AdminPageHeader from '../../components/AdminPageHeader';
import { formatEUR } from '../../utils/format';

const fmtEur = (n) => `${formatEUR((Number(n) || 0))}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};
const hoursAgo = (iso) => {
  if (!iso) return null;
  try {
    const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
    if (h < 1) return 'gerade eben';
    if (h < 24) return `vor ${Math.round(h)} h`;
    return `vor ${Math.round(h / 24)} Tagen`;
  } catch {
    return null;
  }
};

const AbandonedCartsManager = () => {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/abandoned-carts');
      setCarts(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Laden fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendReminder = async (cart) => {
    setBusyId(cart.id);
    try {
      const r = await axiosInstance.post(
        `/admin/abandoned-carts/${cart.id}/send-reminder?kind=${cart.kind}`,
      );
      toast.success(r.data?.message || 'Erinnerung versendet');
      await load();
    } catch (e) {
      toast.error('Versand fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (cart) => {
    if (!window.confirm('Warenkorb aus der Liste entfernen?\n(Der eigentliche Warenkorb des Kunden bleibt unverändert.)')) return;
    setBusyId(cart.id);
    try {
      await axiosInstance.delete(`/admin/abandoned-carts/${cart.id}?kind=${cart.kind}`);
      toast.success('Aus Liste entfernt');
      await load();
    } catch (e) {
      toast.error('Entfernen fehlgeschlagen');
    } finally {
      setBusyId(null);
    }
  };

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await axiosInstance.post('/admin/abandoned-carts/sweep-now');
      toast.success(`Sweep abgeschlossen: ${r.data?.sent || 0} versendet, ${r.data?.skipped || 0} übersprungen`);
      await load();
    } catch (e) {
      toast.error('Sweep fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSweeping(false);
    }
  };

  const totalValue = carts.reduce((sum, c) => sum + (c.subtotal || 0), 0);

  return (
    <div className="min-h-screen py-6 px-4" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-5xl mx-auto">
        <AdminPageHeader
          title="Verlassene Warenkörbe"
          icon={ShoppingCart}
          description="Hier siehst du Warenkörbe, in denen Kunden Artikel zurückgelassen haben. Die KI verschickt automatisch nach 24 h eine freundliche Erinnerungs-Email, wenn die Email-Adresse bekannt ist."
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white border border-[#E8DCC8] p-3" data-testid="stat-total-carts">
            <p className="text-[11px] uppercase tracking-wider text-[#9C8C7A]">Offene Warenkörbe</p>
            <p className="text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>{carts.length}</p>
          </div>
          <div className="bg-white border border-[#E8DCC8] p-3" data-testid="stat-total-value">
            <p className="text-[11px] uppercase tracking-wider text-[#9C8C7A]">Möglicher Umsatz</p>
            <p className="text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>{fmtEur(totalValue)}</p>
          </div>
          <div className="bg-white border border-[#E8DCC8] p-3">
            <p className="text-[11px] uppercase tracking-wider text-[#9C8C7A]">Schon erinnert</p>
            <p className="text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
              {carts.filter((c) => c.reminder_count > 0).length}
            </p>
          </div>
          <div className="bg-white border border-[#E8DCC8] p-3">
            <p className="text-[11px] uppercase tracking-wider text-[#9C8C7A]">Noch zu erinnern</p>
            <p className="text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
              {carts.filter((c) => !c.reminder_count && c.email).length}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <p className="text-xs text-[#6D5A47]">
            <strong>Tipp:</strong> Im Hintergrund läuft alle 30 Minuten ein automatischer Check.
            Du kannst hier aber auch manuell &bdquo;Jetzt prüfen&ldquo; klicken oder einzelne Erinnerungen sofort senden.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={runSweep}
              disabled={sweeping}
              data-testid="run-sweep-btn"
              className="border-[#C4A084] text-[#5D4E3C]"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${sweeping ? 'animate-spin' : ''}`} />
              {sweeping ? 'Prüfe…' : 'Jetzt prüfen'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="reload-btn"
              className="border-[#C4A084] text-[#5D4E3C]"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading && carts.length === 0 ? (
          <div className="bg-white border border-[#E8DCC8] p-10 text-center">
            <p className="text-[#8B7355]">Lädt …</p>
          </div>
        ) : carts.length === 0 ? (
          <div className="bg-white border border-[#E8DCC8] p-10 text-center" data-testid="no-carts">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-600 mb-3 opacity-50" />
            <p className="text-[#5D4E3C] font-medium">Aktuell keine verlassenen Warenkörbe! 🎉</p>
            <p className="text-sm text-[#8B7355] mt-1">Alle Kunden haben entweder bestellt oder noch keine Artikel hinzugefügt.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {carts.map((c) => (
              <div
                key={`${c.kind}-${c.id}`}
                className="bg-white border border-[#E8DCC8] p-4"
                data-testid={`abandoned-cart-${c.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[10px] tracking-wider uppercase px-1.5 py-0.5 ${
                          c.kind === 'user'
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-amber-100 text-amber-800 border border-amber-300'
                        }`}
                      >
                        {c.kind === 'user' ? <User className="h-3 w-3 inline mr-0.5" /> : null}
                        {c.kind === 'user' ? 'Kunde' : 'Gast'}
                      </span>
                      {c.reminder_count > 0 && (
                        <span className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 bg-green-100 text-green-800 border border-green-300">
                          <CheckCircle2 className="h-3 w-3 inline mr-0.5" />
                          Erinnert {fmtDate(c.reminder_sent_at)}
                        </span>
                      )}
                      {!c.email && (
                        <span className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300">
                          <AlertCircle className="h-3 w-3 inline mr-0.5" />
                          Keine Email
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-[#5D4E3C] mt-1.5" style={{ fontFamily: 'Georgia, serif' }}>
                      {c.email || '— keine Email hinterlegt —'}
                      {c.user_name && <span className="text-[#9C8C7A] font-normal text-sm ml-2">({c.user_name})</span>}
                    </p>
                    <p className="text-xs text-[#8B7355]">
                      <strong>{c.items_count}</strong> {c.items_count === 1 ? 'Artikel' : 'Artikel'} ·
                      Wert <strong>{fmtEur(c.subtotal)}</strong> ·
                      Aktualisiert {hoursAgo(c.updated_at)} ({fmtDate(c.updated_at)})
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {c.email && (
                      <Button
                        size="sm"
                        onClick={() => sendReminder(c)}
                        disabled={busyId === c.id}
                        className="bg-[#5D4E3C] hover:bg-[#4A3E30] text-white"
                        data-testid={`send-reminder-${c.id}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        {busyId === c.id ? 'Sende…' : (c.reminder_count > 0 ? 'Nochmal senden' : 'Jetzt erinnern')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismiss(c)}
                      disabled={busyId === c.id}
                      className="text-[#8B7355] hover:text-red-700"
                      title="Aus Liste entfernen (Warenkorb des Kunden bleibt)"
                      data-testid={`dismiss-cart-${c.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Items preview */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F0E7D8]">
                  {(c.items || []).slice(0, 8).map((it, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#FAF5EE] border border-[#E8DCC8] text-[#5D4E3C]"
                    >
                      <ShoppingCart className="h-3 w-3 text-[#C4A084]" />
                      {it.quantity}× {it.product_id?.slice(0, 12) || 'Artikel'}
                      {it.price ? <span className="text-[#9C8C7A] ml-1">({fmtEur(it.price * it.quantity)})</span> : null}
                    </span>
                  ))}
                  {(c.items || []).length > 8 && (
                    <span className="text-xs text-[#9C8C7A] px-2 py-1">+ {c.items.length - 8} weitere</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 p-3 bg-[#FAF5EE] border-l-4 border-[#C4A084] text-xs text-[#6D5A47] leading-relaxed">
          <p className="font-semibold mb-1 flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-[#C4A084]" />
            Was passiert hier?
          </p>
          <p>
            Wenn ein Kunde Artikel in den Warenkorb legt und 24 Stunden nichts passiert,
            schickt das System eine freundliche Erinnerungs-Email mit Foto, Preis und einem
            Link zurück zum Warenkorb. Pro Warenkorb wird automatisch nur EINE Erinnerung
            verschickt — kein Spam. Wenn der Kunde bestellt, verschwindet der Warenkorb hier
            automatisch.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AbandonedCartsManager;
